import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  CheckCircle2,
  ClipboardList,
  Download,
  Fuel,
  Gauge,
  MapPin,
  Plus,
  RefreshCw,
  Send,
  Truck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { controlFuelAPI } from '../lib/api';
import { downloadBlob } from '../lib/download';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatMontant } from '../lib/formats';
import { libelle, referenceLisible } from '../lib/libelles';
import { firstControlFuelRoute, visibleControlFuelItems } from '../lib/entitlements';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import BusinessActionContract from '../components/business/BusinessActionContract';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import {
  fuelIssueBlockers,
  fuelIssueContract,
  fuelRequestBlockers,
  fuelRequestContract,
} from '../lib/businessContracts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';

const PAGE_CONFIG = {
  '/control-fuel': {
    key: 'dashboard',
    module: 'fuel_consumption',
    // "Tableau de bord" ne disait rien : toutes les pages du produit en sont
    // un. Le titre nomme maintenant le sujet — le carburant que la société
    // consomme elle-même, par opposition à celui qu'elle transporte ou revend.
    title: 'Carburant interne',
    eyebrow: 'Contrôle',
    description: 'Ce qui sort des cuves, pour qui, et à quel coût.',
    icon: BarChart3,
  },
  '/control-fuel/sites': {
    key: 'sites',
    module: 'sites',
    title: 'Sites de consommation',
    eyebrow: 'Chantiers, bases et dépôts',
    description: 'Suivre où le carburant est consommé et combien chaque site coûte.',
    icon: Building2,
  },
  '/control-fuel/assets': {
    key: 'assets',
    module: 'assets',
    title: 'Engins et bénéficiaires',
    eyebrow: 'Matériel interne',
    description: 'Lire la consommation par engin, compteur et rattachement site.',
    icon: Truck,
  },
  '/control-fuel/requests': {
    key: 'requests',
    module: 'fuel_requests',
    title: 'Demandes carburant',
    eyebrow: 'Validation avant distribution',
    description: 'Estimer la dépense avant d’autoriser une sortie.',
    icon: Send,
  },
  '/control-fuel/issues': {
    key: 'issues',
    module: 'fuel_issues',
    title: 'Sorties carburant',
    eyebrow: 'Distribution interne',
    description: 'Enregistrer les litres sortis, le prix du litre et la dépense réelle.',
    icon: Fuel,
  },
  '/control-fuel/quotas': {
    key: 'quotas',
    module: 'fuel_quotas',
    title: 'Quotas',
    eyebrow: 'Limites et seuils',
    description: 'Comparer les plafonds aux consommations réelles.',
    icon: Gauge,
  },
  '/control-fuel/history': {
    key: 'history',
    module: 'fuel_consumption',
    title: 'Rapports carburant',
    eyebrow: 'Historique exportable',
    description: 'Analyser les sorties, les demandes et exporter le rapport PDF.',
    icon: ClipboardList,
  },
};

const emptyData = {
  sites: [],
  assets: [],
  requests: [],
  issues: [],
  quotas: [],
  summary: {},
};

const number = (value) => Number(value || 0).toLocaleString('fr-FR');
const money = (value) => formatMontant(Number(value || 0));
const liters = (value) => `${number(value)} L`;
const costLabel = (value) => (Number(value || 0) > 0 ? money(value) : 'Prix non renseigné');
const dateTime = (value) => formatDateHeure(value, 'Date non disponible');

export default function ControlFuelPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasModule } = useAuth();
  const config = PAGE_CONFIG[location.pathname] || PAGE_CONFIG['/control-fuel'];
  const allowedNavItems = useMemo(() => visibleControlFuelItems(hasModule), [hasModule]);
  const isAllowed = hasModule(config.module);
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [siteForm, setSiteForm] = useState({ name: '', type: 'chantier', city: '' });
  const [assetForm, setAssetForm] = useState({ name: '', code: '', asset_type: 'machine', site_id: '' });
  const [requestForm, setRequestForm] = useState({ request_number: '', site_id: '', asset_id: '', requested_quantity: '', unit_price: '', reason: '' });
  const [issueForm, setIssueForm] = useState({ site_id: '', asset_id: '', fuel_request_id: '', quantity: '', unit_price: '', received_by_name: '', odometer_or_hours: '' });
  const [quotaForm, setQuotaForm] = useState({ scope_type: 'site', scope_id: '', period: 'monthly', quantity_limit: '', alert_threshold: '' });

  const load = useCallback(async () => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = { ...emptyData };
      const tasks = [];
      if (hasModule('sites')) tasks.push(controlFuelAPI.getSites().then((sites) => { next.sites = sites; }));
      if (hasModule('assets')) tasks.push(controlFuelAPI.getAssets().then((assets) => { next.assets = assets; }));
      if (hasModule('fuel_requests')) tasks.push(controlFuelAPI.getRequests().then((requests) => { next.requests = requests; }));
      if (hasModule('fuel_issues')) tasks.push(controlFuelAPI.getIssues().then((issues) => { next.issues = issues; }));
      if (hasModule('fuel_quotas')) tasks.push(controlFuelAPI.getQuotas().then((quotas) => { next.quotas = quotas; }));
      if (hasModule('fuel_consumption')) tasks.push(controlFuelAPI.getSummary().then((summary) => { next.summary = summary; }));
      await Promise.all(tasks);
      setData(next);
    } finally {
      setLoading(false);
    }
  }, [hasModule, isAllowed]);

  useEffect(() => {
    load().catch((error) => {
      if (error?.response?.status === 403) {
        toast.error('Ce module n’est pas activé pour votre société');
      } else {
        toast.error('Impossible de charger le contrôle carburant');
      }
    });
  }, [load, location.pathname, isAllowed]);

  const siteById = useMemo(() => Object.fromEntries(data.sites.map((site) => [site.id, site])), [data.sites]);
  const assetById = useMemo(() => Object.fromEntries(data.assets.map((asset) => [asset.id, asset])), [data.assets]);
  const approvedRequests = data.requests.filter((request) => request.status === 'approved');
  const pendingRequests = data.requests.filter((request) => !['approved', 'rejected'].includes(request.status));
  const latestEvents = [...data.requests, ...data.issues].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  const issueCostPreview = Number(issueForm.quantity || 0) * Number(issueForm.unit_price || 0);
  const requestCostPreview = Number(requestForm.requested_quantity || 0) * Number(requestForm.unit_price || 0);
  const requestContract = fuelRequestContract(requestForm);
  const issueContract = fuelIssueContract(issueForm);
  const siteSpend = useMemo(() => Object.fromEntries((data.summary?.by_site || []).map((row) => [row.site_id, row])), [data.summary]);
  const assetSpend = useMemo(() => Object.fromEntries((data.summary?.by_asset || []).map((row) => [row.asset_id, row])), [data.summary]);
  const quotaWatchCount = data.quotas.filter((quota) => quota.is_active && Number(quota.alert_threshold || 0) > 0).length;
  // Sur un pack neuf, quatre pastilles vertes cochées annonçaient « tout va
  // bien » alors que la vérité est « rien n'est configuré ». Ce n'est pas le
  // même message et ça n'appelle pas la même action.
  const isUnconfigured = data.sites.length === 0 && data.assets.length === 0;

  const submit = async (event, action, reset, message) => {
    event.preventDefault();
    try {
      await action();
      reset();
      await load();
      toast.success(message);
    } catch (error) {
      toast.error(messageErreur(error, 'L’enregistrement n’a pas abouti'));
    }
  };

  const approve = async (request) => {
    try {
      await controlFuelAPI.approveRequest(request.id);
      await load();
      toast.success('Demande approuvée');
    } catch (error) {
      toast.error(messageErreur(error, 'La demande n’a pas pu être approuvée'));
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await controlFuelAPI.downloadReportPdf();
      downloadBlob(response, 'rapport-controle-carburant.pdf', 'application/pdf');
      toast.success('Rapport PDF généré');
    } catch (error) {
      toast.error(messageErreur(error, 'Export PDF indisponible'));
    }
  };

  if (!isAllowed) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Card className="border-flame/30 bg-flame/10">
          <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-black uppercase text-flame-ink">Module non activé pour votre société</p>
              <div className="mt-1 flex items-center gap-1">
                <h1>{config.title}</h1>
                <Aide titre="Module non activé">Cette page est masquée tant que le module correspondant n’est pas activé pour votre société.</Aide>
              </div>
            </div>
            <Button onClick={() => navigate(firstControlFuelRoute(hasModule))}>Voir une page autorisée</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Une valeur absente s'écrit « — » et son explication va dans l'aide: ces
  // puces recevaient des PHRASES (« Prix non renseigné ») que le chiffre
  // coupait en plein mot. `quotaWatchCount` compte les quotas qui ONT un seuil
  // d'alerte, pas ceux qui le dépassent: pas de teinte d'alerte.
  const statsEntete =
    config.key === 'dashboard'
      ? [
          { label: 'Litres sortis', valeur: Number(data.summary?.total_quantity) || 0, unite: 'L' },
          {
            label: 'Dépense carburant',
            valeur: Number(data.summary?.total_spend) || null,
            unite: 'FCFA',
            aide: Number(data.summary?.total_spend) > 0 ? undefined : 'Aucune sortie avec un prix',
          },
          {
            label: 'À approuver',
            valeur: pendingRequests.length,
            ton: pendingRequests.length ? 'attention' : 'neutre',
            aide: pendingRequests.length ? 'Bloque les sorties' : undefined,
          },
          {
            label: 'Quotas surveillés',
            valeur: quotaWatchCount,
            aide: quotaWatchCount ? 'Avec seuil d’alerte' : 'Aucun seuil défini',
          },
        ]
      : undefined;
  const prixMoyen = Number(data.summary?.average_unit_price) || 0;
  const statsPlus =
    config.key === 'dashboard' ? (
      <p className="text-sm">
        <span className="text-muted-foreground">Prix moyen : </span>
        <span className="font-medium tabular-nums">
          {prixMoyen > 0 ? `${prixMoyen.toLocaleString('fr-FR')} FCFA / L` : 'Prix non renseigné'}
        </span>
      </p>
    ) : undefined;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        description={config.description}
        stats={statsEntete}
        statsPlus={statsPlus}
        actions={
          <>
            {hasModule('fuel_consumption') && (
              <Button variant="outline" size="sm" onClick={downloadPdf}>
                <Download className="h-4 w-4" aria-hidden="true" />
                PDF
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </>
        }
      />

      {/* Ces liens sont déjà tous dans le sidebar, sous « Contrôle carburant » :
          les répéter ici volait une rangée entière de l'écran pour rien. Ils ne
          restent que sous lg, là où le sidebar est replié dans un tiroir et
          n'est donc pas visible. */}
      <nav className="flex flex-wrap gap-2 lg:hidden" aria-label="Sections du carburant interne">
        {allowedNavItems.map((item) => (
          <Button
            key={item.to}
            type="button"
            variant={location.pathname === item.to ? 'default' : 'outline'}
            size="sm"
            onClick={() => navigate(item.to)}
            aria-current={location.pathname === item.to ? 'page' : undefined}
          >
            {item.label}
          </Button>
        ))}
      </nav>

      {config.key === 'dashboard' && (
        <>
          <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            {/* « Demandes à approuver » et « Dépense enregistrée » répétaient
                mot pour mot deux readouts affichés deux cents pixels plus
                haut, avec la même phrase tronquée. Ne restent ici que les
                deux entrées qui apportent une navigation que la bande de
                readouts ne donne pas. */}
            <Panel title="Référentiel" description="Ce qui doit être déclaré avant de pouvoir sortir du carburant.">
              {isUnconfigured ? (
                <div className="rounded-xl border border-dashed border-border p-4">
                  <div className="flex items-center gap-1">
                    <p className="font-semibold text-foreground">Rien n’est encore déclaré</p>
                    <Aide titre="Rien n’est encore déclaré">
                      Une sortie de cuve se rattache à un site et à un engin. Commencez par
                      déclarer au moins un de chaque.
                    </Aide>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => navigate('/control-fuel/sites')}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Ajouter un site
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate('/control-fuel/assets')}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Ajouter un engin
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  <ActionBox title="Sites" value={data.sites.length} text="Bases, chantiers ou dépôts internes." onClick={() => navigate('/control-fuel/sites')} />
                  <ActionBox title="Engins" value={data.assets.length} text="Matériel consommateur de carburant." onClick={() => navigate('/control-fuel/assets')} />
                </div>
              )}
            </Panel>
            <Panel title="Dernières opérations" description="Demandes et sorties récentes avec leur impact.">
              {latestEvents.slice(0, 7).map((item) => (
                <FuelRow
                  key={`${item.id}-${item.request_number || item.quantity}`}
                  title={item.request_number || `${liters(item.quantity)} sortis`}
                  meta={item.request_number ? `${siteById[item.site_id]?.name || 'Site'} · estimation ${costLabel(item.total_cost)}` : `${siteById[item.site_id]?.name || 'Site'} · ${assetById[item.asset_id]?.name || item.received_by_name || 'Bénéficiaire'} · ${costLabel(item.total_cost)}`}
                  right={<Badge variant="outline">{item.request_number ? 'Demande' : 'Sortie'}</Badge>}
                />
              ))}
              {latestEvents.length === 0 && (
                <Empty
                  icon={ClipboardList}
                  title="Aucune opération carburant"
                  text="Ce module suit le carburant que vous distribuez en interne : qui a pris combien de litres, pour quel engin et sur quel chantier. Chaque demande et chaque sortie apparaîtra ici."
                  prerequisite={
                    data.sites.length === 0
                      ? { label: 'Créer un site d’abord', to: '/control-fuel/sites' }
                      : data.assets.length === 0
                        ? { label: 'Déclarer un engin d’abord', to: '/control-fuel/assets' }
                        : { label: 'Créer une demande', to: '/control-fuel/requests' }
                  }
                />
              )}
            </Panel>
          </div>
        </>
      )}

      {config.key === 'sites' && (
        <CrudPanel title="Nouveau site" description="Un site représente un chantier, une base ou un dépôt interne." form={(
          <form onSubmit={(event) => submit(event, () => controlFuelAPI.createSite({ ...siteForm, status: 'active' }), () => setSiteForm({ name: '', type: 'chantier', city: '' }), 'Site créé')} className="grid gap-3 md:grid-cols-4">
            <Input placeholder="Nom du site" value={siteForm.name} onChange={(event) => setSiteForm({ ...siteForm, name: event.target.value })} required />
            <Input placeholder="Ville" value={siteForm.city} onChange={(event) => setSiteForm({ ...siteForm, city: event.target.value })} />
            <Input placeholder="Type de site" value={siteForm.type} onChange={(event) => setSiteForm({ ...siteForm, type: event.target.value })} />
            <Button type="submit"><Plus className="mr-2 h-4 w-4" /> Ajouter</Button>
          </form>
        )}>
          <div className="grid gap-3 p-3 lg:grid-cols-2">
            {data.sites.map((site) => {
              const spend = siteSpend[site.id] || {};
              const assetsCount = data.assets.filter((asset) => asset.site_id === site.id).length;
              return (
                <InfoCard key={site.id} title={site.name} icon={MapPin} badge={<StatusBadge status={site.status} />}>
                  <InfoLine label="Ville" value={site.city || 'Non renseignée'} />
                  <InfoLine label="Engins rattachés" value={assetsCount} />
                  <InfoLine label="Litres consommés" value={liters(spend.quantity)} />
                  <InfoLine label="Dépense site" value={costLabel(spend.spend)} strong />
                </InfoCard>
              );
            })}
          </div>
          {/* Le formulaire de création est juste au-dessus: pas de bouton,
              rien de plus court que le geste déjà à l'écran. */}
          {data.sites.length === 0 && (
            <Empty
              icon={MapPin}
              title="Aucun site de consommation"
              text="Un site est un endroit où le carburant est consommé : un chantier, une base, un dépôt interne. C’est lui qui permet de savoir où part le carburant. Créez le premier avec le formulaire ci-dessus."
            />
          )}
        </CrudPanel>
      )}

      {config.key === 'assets' && (
        <CrudPanel title="Nouvel engin" description="Un engin rattaché à un site permet de suivre consommation, dépense et compteur." form={(
          <form onSubmit={(event) => submit(event, () => controlFuelAPI.createAsset({ ...assetForm, status: 'active' }), () => setAssetForm({ name: '', code: '', asset_type: 'machine', site_id: '' }), 'Engin créé')} className="grid gap-3 md:grid-cols-5">
            <Input placeholder="Nom engin" value={assetForm.name} onChange={(event) => setAssetForm({ ...assetForm, name: event.target.value })} required />
            <Input placeholder="Code interne" value={assetForm.code} onChange={(event) => setAssetForm({ ...assetForm, code: event.target.value })} required />
            <Select value={assetForm.asset_type} onValueChange={(asset_type) => setAssetForm({ ...assetForm, asset_type })}>
              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{['truck', 'machine', 'generator', 'vehicle'].map((value) => <SelectItem key={value} value={value}>{libelle('engineType', value)}</SelectItem>)}</SelectContent>
            </Select>
            <EntitySelect value={assetForm.site_id} onChange={(site_id) => setAssetForm({ ...assetForm, site_id })} items={data.sites} placeholder="Site" />
            <Button type="submit"><Plus className="mr-2 h-4 w-4" /> Ajouter</Button>
          </form>
        )}>
          <div className="grid gap-3 p-3 lg:grid-cols-3">
            {data.assets.map((asset) => {
              const spend = assetSpend[asset.id] || {};
              return (
                <InfoCard key={asset.id} title={asset.name} icon={Truck} badge={<Badge>{libelle('engineType', asset.asset_type)}</Badge>}>
                  <InfoLine label="Code" value={asset.code} />
                  <InfoLine label="Site" value={siteById[asset.site_id]?.name || 'Site non renseigné'} />
                  <InfoLine label="Litres consommés" value={liters(spend.quantity)} />
                  <InfoLine label="Dépense engin" value={costLabel(spend.spend)} strong />
                </InfoCard>
              );
            })}
          </div>
          {data.assets.length === 0 && (
            <Empty
              icon={Truck}
              title="Aucun engin suivi"
              text="Un engin est ce qui consomme : groupe électrogène, pelle, camion de chantier. Le rattacher à un site permet de comparer les consommations et de repérer celui qui boit trop."
              prerequisite={
                data.sites.length === 0
                  ? { label: 'Créer un site d’abord', to: '/control-fuel/sites' }
                  : undefined
              }
            />
          )}
        </CrudPanel>
      )}

      {config.key === 'requests' && (
        <CrudPanel title="Nouvelle demande" description="Le prix du litre donne une estimation avant validation." form={(
          <form onSubmit={(event) => submit(event, () => controlFuelAPI.createRequest({ ...requestForm, requested_quantity: Number(requestForm.requested_quantity), unit_price: requestForm.unit_price ? Number(requestForm.unit_price) : null, status: 'pending' }), () => setRequestForm({ request_number: '', site_id: '', asset_id: '', requested_quantity: '', unit_price: '', reason: '' }), 'Demande créée')} className="grid gap-3 md:grid-cols-7">
            <Input placeholder="N° demande" value={requestForm.request_number} onChange={(event) => setRequestForm({ ...requestForm, request_number: event.target.value })} required />
            <EntitySelect value={requestForm.site_id} onChange={(site_id) => setRequestForm({ ...requestForm, site_id })} items={data.sites} placeholder="Site" />
            <EntitySelect value={requestForm.asset_id} onChange={(asset_id) => setRequestForm({ ...requestForm, asset_id })} items={data.assets} placeholder="Engin" />
            <Input type="number" placeholder="Litres" value={requestForm.requested_quantity} onChange={(event) => setRequestForm({ ...requestForm, requested_quantity: event.target.value })} required />
            <Input type="number" placeholder="Prix / litre" value={requestForm.unit_price} onChange={(event) => setRequestForm({ ...requestForm, unit_price: event.target.value })} />
            <Input placeholder="Motif" value={requestForm.reason} onChange={(event) => setRequestForm({ ...requestForm, reason: event.target.value })} />
            <div className="md:col-span-7">
              <BusinessActionContract
                title="Vérifications de la demande de carburant"
                contract={requestContract}
                blockers={fuelRequestBlockers(requestForm)}
                compact
              />
            </div>
            <Button type="submit"><Plus className="mr-2 h-4 w-4" /> Créer</Button>
          </form>
        )}>
          <InlineHint label="Estimation de la demande" value={costLabel(requestCostPreview)} />
          {data.requests.map((request) => (
            <FuelRow key={request.id} title={referenceLisible(request.request_number)} meta={`${siteById[request.site_id]?.name || 'Site'} · ${liters(request.requested_quantity)} · estimation ${costLabel(request.total_cost)} · ${request.reason || 'Sans motif'}`} right={<><StatusBadge status={request.status} />{request.status !== 'approved' && <Button size="sm" onClick={() => approve(request)}>Approuver</Button>}</>} />
          ))}
          {data.requests.length === 0 && (
            <Empty
              icon={Send}
              title="Aucune demande de carburant"
              text="Une demande est ce qu’un chef de chantier réclame avant de servir : combien de litres, pour quel engin, pourquoi. Approuvée, elle autorise la sortie."
              prerequisite={
                data.sites.length === 0
                  ? { label: 'Créer un site d’abord', to: '/control-fuel/sites' }
                  : data.assets.length === 0
                    ? { label: 'Déclarer un engin d’abord', to: '/control-fuel/assets' }
                    : undefined
              }
            />
          )}
        </CrudPanel>
      )}

      {config.key === 'issues' && (
        <CrudPanel title="Nouvelle sortie carburant" description="Chaque sortie crée un mouvement de stock et une dépense réelle." form={(
          <form onSubmit={(event) => submit(event, () => controlFuelAPI.createIssue({ ...issueForm, quantity: Number(issueForm.quantity), unit_price: issueForm.unit_price ? Number(issueForm.unit_price) : null, odometer_or_hours: issueForm.odometer_or_hours ? Number(issueForm.odometer_or_hours) : null }), () => setIssueForm({ site_id: '', asset_id: '', fuel_request_id: '', quantity: '', unit_price: '', received_by_name: '', odometer_or_hours: '' }), 'Sortie enregistrée')} className="grid gap-3 md:grid-cols-7">
            <EntitySelect value={issueForm.site_id} onChange={(site_id) => setIssueForm({ ...issueForm, site_id })} items={data.sites} placeholder="Site" />
            <EntitySelect value={issueForm.asset_id} onChange={(asset_id) => setIssueForm({ ...issueForm, asset_id })} items={data.assets} placeholder="Engin" />
            <EntitySelect value={issueForm.fuel_request_id} onChange={(fuel_request_id) => setIssueForm({ ...issueForm, fuel_request_id })} items={approvedRequests} labelKey="request_number" placeholder="Demande approuvée" />
            <Input type="number" placeholder="Litres" value={issueForm.quantity} onChange={(event) => setIssueForm({ ...issueForm, quantity: event.target.value })} required />
            <Input type="number" placeholder="Prix / litre" value={issueForm.unit_price} onChange={(event) => setIssueForm({ ...issueForm, unit_price: event.target.value })} />
            <Input placeholder="Bénéficiaire" value={issueForm.received_by_name} onChange={(event) => setIssueForm({ ...issueForm, received_by_name: event.target.value })} />
            <div className="md:col-span-7">
              <BusinessActionContract
                title="Vérifications de la sortie de carburant"
                contract={issueContract}
                blockers={fuelIssueBlockers(issueForm)}
                compact
              />
            </div>
            <Button type="submit"><Fuel className="mr-2 h-4 w-4" /> Enregistrer</Button>
          </form>
        )}>
          <InlineHint label="Dépense calculée" value={costLabel(issueCostPreview)} />
          {data.issues.map((issue) => (
            <FuelRow key={issue.id} title={`${liters(issue.quantity)} · ${costLabel(issue.total_cost)}`} meta={`${siteById[issue.site_id]?.name || 'Site'} · ${assetById[issue.asset_id]?.name || issue.received_by_name || 'Bénéficiaire'} · prix ${issue.unit_price ? `${money(issue.unit_price)} / L` : 'non renseigné'}`} right={<Badge variant="outline">Sortie validée</Badge>} />
          ))}
          {data.issues.length === 0 && (
            <Empty
              icon={Fuel}
              title="Aucune sortie carburant"
              text="Une sortie est le carburant réellement servi : elle diminue le stock et crée la dépense. C’est ce chiffre-là, et non la demande, qui compte à la fin du mois."
              prerequisite={
                data.sites.length === 0
                  ? { label: 'Créer un site d’abord', to: '/control-fuel/sites' }
                  : data.assets.length === 0
                    ? { label: 'Déclarer un engin d’abord', to: '/control-fuel/assets' }
                    : undefined
              }
            />
          )}
        </CrudPanel>
      )}

      {config.key === 'quotas' && (
        <CrudPanel title="Nouveau quota" description="Un quota rend visible les limites et seuils d’alerte." form={(
          <form onSubmit={(event) => submit(event, () => controlFuelAPI.createQuota({ ...quotaForm, quantity_limit: Number(quotaForm.quantity_limit), alert_threshold: Number(quotaForm.alert_threshold || 0), is_active: true }), () => setQuotaForm({ scope_type: 'site', scope_id: '', period: 'monthly', quantity_limit: '', alert_threshold: '' }), 'Quota créé')} className="grid gap-3 md:grid-cols-6">
            <Select value={quotaForm.scope_type} onValueChange={(scope_type) => setQuotaForm({ ...quotaForm, scope_type, scope_id: '' })}>
              <SelectTrigger><SelectValue placeholder="Portée" /></SelectTrigger>
              <SelectContent>{['site', 'asset'].map((value) => <SelectItem key={value} value={value}>{libelle('quotaScope', value)}</SelectItem>)}</SelectContent>
            </Select>
            <EntitySelect value={quotaForm.scope_id} onChange={(scope_id) => setQuotaForm({ ...quotaForm, scope_id })} items={quotaForm.scope_type === 'asset' ? data.assets : data.sites} placeholder={quotaForm.scope_type === 'asset' ? 'Engin' : 'Site'} />
            <Select value={quotaForm.period} onValueChange={(period) => setQuotaForm({ ...quotaForm, period })}>
              <SelectTrigger><SelectValue placeholder="Période" /></SelectTrigger>
              <SelectContent>{['daily', 'weekly', 'monthly'].map((value) => <SelectItem key={value} value={value}>{libelle('quotaPeriod', value)}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Limite litres" value={quotaForm.quantity_limit} onChange={(event) => setQuotaForm({ ...quotaForm, quantity_limit: event.target.value })} required />
            <Input type="number" placeholder="Seuil alerte" value={quotaForm.alert_threshold} onChange={(event) => setQuotaForm({ ...quotaForm, alert_threshold: event.target.value })} />
            <Button type="submit"><Plus className="mr-2 h-4 w-4" /> Ajouter</Button>
          </form>
        )}>
          <div className="grid gap-3 p-3 md:grid-cols-2">
            {data.quotas.map((quota) => (
              <InfoCard key={quota.id} title={`${libelle('quotaPeriod', quota.period)} · ${liters(quota.quantity_limit)}`} icon={Gauge} badge={<StatusBadge status={quota.is_active ? 'active' : 'inactive'} />}>
                <InfoLine label="Portée" value={`${libelle('quotaScope', quota.scope_type)} · ${siteById[quota.scope_id]?.name || assetById[quota.scope_id]?.name || 'Non renseigné'}`} />
                <InfoLine label="Seuil d’alerte" value={liters(quota.alert_threshold)} />
                <InfoLine label="Action" value="Surveiller la consommation réelle avant dépassement." />
              </InfoCard>
            ))}
          </div>
          {data.quotas.length === 0 && (
            <Empty
              icon={Gauge}
              title="Aucun quota configuré"
              text="Un quota fixe la limite de litres qu’un site ou un engin peut consommer sur une période, et alerte avant le dépassement. Sans quota, une dérive ne se voit qu’à la facture."
              prerequisite={
                data.sites.length === 0 && data.assets.length === 0
                  ? { label: 'Créer un site d’abord', to: '/control-fuel/sites' }
                  : undefined
              }
            />
          )}
        </CrudPanel>
      )}

      {config.key === 'history' && (
        <Panel title="Historique et rapport PDF" description="Demandes et sorties classées de la plus récente à la plus ancienne.">
          <div className="flex flex-col gap-3 rounded-xl border border-flame/20 bg-flame/10 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-1">
                <p className="font-black text-foreground">Dépense totale: {costLabel(data.summary?.total_spend)}</p>
                <Aide titre="Rapport PDF">Le PDF reprend les litres, dépenses, sites, engins et dernières sorties.</Aide>
              </div>
            </div>
            <Button onClick={downloadPdf}><Download className="mr-2 h-4 w-4" /> Exporter PDF</Button>
          </div>
          {latestEvents.map((item) => (
            <FuelRow key={`${item.request_number || item.id}-${item.id}`} title={item.request_number || `${liters(item.quantity)} sortis`} meta={`${dateTime(item.created_at)} · ${siteById[item.site_id]?.name || 'Site'} · ${costLabel(item.total_cost)}`} right={<Badge variant="outline">{item.request_number ? 'Demande' : 'Sortie'}</Badge>} />
          ))}
          {latestEvents.length === 0 && (
            <Empty
              icon={ClipboardList}
              title="L’historique est vide"
              text="Demandes et sorties s’empilent ici de la plus récente à la plus ancienne, et alimentent le rapport PDF que vous pouvez remettre à un donneur d’ordre."
              prerequisite={{ label: 'Enregistrer une sortie', to: '/control-fuel/issues' }}
            />
          )}
        </Panel>
      )}
    </div>
  );
}


function Panel({ title, description, children }) {
  return (
    <Card className="border-border bg-card shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-1">
          <CardTitle className="text-lg font-black text-foreground">{title}</CardTitle>
          {description && <Aide titre={title}>{description}</Aide>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function CrudPanel({ title, description, form, children }) {
  return (
    <Panel title={title} description={description}>
      <div className="rounded-xl border border-border bg-muted/50 p-3">{form}</div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">{children}</div>
    </Panel>
  );
}

function FuelRow({ title, meta, right }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border/60 p-4 last:border-b-0 md:flex-row md:items-center md:justify-between">
      <div>
        <p className="font-bold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{meta}</p>
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

function InfoCard({ title, icon: Icon, badge, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-flame/15 text-flame-ink">
            <Icon className="h-5 w-5" />
          </div>
          <p className="font-black text-foreground">{title}</p>
        </div>
        {badge}
      </div>
      <div className="mt-4 space-y-2">{children}</div>
    </div>
  );
}

function InfoLine({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? 'font-black text-foreground' : 'font-semibold text-foreground'}>{value}</span>
    </div>
  );
}

function InlineHint({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-muted/50 px-4 py-3 text-sm">
      <span className="font-semibold text-muted-foreground">{label}</span>
      <span className="font-black text-foreground">{value}</span>
    </div>
  );
}

function ActionBox({ title, value, text, tone = 'neutral', onClick }) {
  const Icon = tone === 'warning' ? AlertTriangle : CheckCircle2;
  return (
    <button type="button" onClick={onClick} className="rounded-xl border border-border bg-muted/50 p-4 text-left transition hover:border-flame/30 hover:bg-flame/10">
      <div className="flex items-center justify-between">
        <p className="font-bold text-foreground">{title}</p>
        <Icon className={`h-5 w-5 ${tone === 'warning' ? 'text-flame-ink' : 'text-success'}`} />
      </div>
      <p className="kpi-value mt-1 text-foreground">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </button>
  );
}

function StatusBadge({ status }) {
  const label = libelle('fuelRequestStatus', status, 'Non renseigné');
  const className = status === 'approved' || status === 'active'
    ? 'bg-success/15 text-success'
    : status === 'pending'
      ? 'bg-flame/15 text-flame-ink'
      : status === 'rejected' || status === 'inactive'
        ? 'bg-destructive/15 text-destructive'
        : 'bg-muted text-muted-foreground';
  return <Badge className={className}>{label}</Badge>;
}

/**
 * Panneau vide d'un onglet Contrôle carburant.
 *
 * Les sept onglets disaient la même chose de sept façons — une phrase grise
 * en huit mots — sans jamais dire ce que l'onglet sert à faire, ni pourquoi
 * il est vide, ni ce qu'il faut d'abord créer. `prerequisite` porte ce
 * dernier point: quand un formulaire dépend d'un site ou d'un engin qui
 * n'existe pas encore, le bouton mène au prérequis et non à un champ
 * obligatoire qui n'aura rien à proposer.
 */
function Empty({ title, text, icon, prerequisite }) {
  const navigate = useNavigate();

  return (
    <EmptyState
      icon={icon || Fuel}
      title={title || 'Rien à afficher'}
      description={text}
      actionLabel={prerequisite?.label}
      onAction={prerequisite ? () => navigate(prerequisite.to) : undefined}
      className="bg-muted/50"
    />
  );
}

function EntitySelect({ value, onChange, items, placeholder, labelKey = 'name' }) {
  const options = useMemo(
    () => items.map((item) => ({
      value: item.id,
      label: String(item[labelKey] || item.name || 'Sans nom'),
      description: [item.code, item.city].filter(Boolean).join(' · ') || undefined,
      keywords: [item.name],
    })),
    [items, labelKey],
  );
  return (
    <SearchableSelect
      value={value || ''}
      onValueChange={onChange}
      options={options}
      placeholder={placeholder}
    />
  );
}
